import { useState, useEffect } from "react";
import Select from "@/components/base/Select";
import Toggle from "@/components/base/Toggle";
import type { Provider, WorkDay } from "@/lib/provider";
import { supabase } from "@/lib/supabase";

const DEFAULT_DAYS: { key: string; label: string }[] = [
  { key: "sat", label: "السبت" },
  { key: "sun", label: "الأحد" },
  { key: "mon", label: "الاثنين" },
  { key: "tue", label: "الثلاثاء" },
  { key: "wed", label: "الأربعاء" },
  { key: "thu", label: "الخميس" },
  { key: "fri", label: "الجمعة" },
];

const SERVICE_META: Record<string, { label: string; durations: string[] }> = {
  car_wash: { label: "غسيل سيارات", durations: ["20 دقيقة", "30 دقيقة", "40 دقيقة", "60 دقيقة"] },
  home_cleaning: { label: "تنظيف منازل", durations: ["2 ساعة", "3 ساعات", "4 ساعات", "6 ساعات"] },
  laundry: { label: "مغسلة ملابس", durations: ["24 ساعة", "48 ساعة", "72 ساعة"] },
};

interface Props {
  provider: Provider | null;
}

export default function WorkingHoursTab({ provider }: Props) {
  const [days, setDays] = useState<WorkDay[]>([]);
  const [durations, setDurations] = useState<Record<string, string>>({});
  const [maxOrders, setMaxOrders] = useState(5);
  const [savedHours, setSavedHours] = useState(false);
  const [savedDurations, setSavedDurations] = useState(false);

  const serviceTypes = provider?.service_types?.length
    ? provider.service_types
    : ["car_wash", "home_cleaning", "laundry"];

  useEffect(() => {
    const stored = provider?.working_hours;
    const base =
      stored && stored.length
        ? stored
        : DEFAULT_DAYS.map((d) => ({ ...d, open: "08:00", close: "18:00", closed: false }));
    setDays(base);
    setDurations(provider?.service_durations ?? {});
    setMaxOrders(provider?.max_concurrent_orders ?? 5);
  }, [provider]);

  const inputClass =
    "h-12 w-full rounded-[14px] border border-[#E4F5F5] bg-[#F0FDFD] px-4 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#00AAB7]";
  const labelClass = "mb-2 block text-sm font-medium text-ghasla-text-primary";

  const toggleClosed = (key: string, closed: boolean) => {
    setDays((prev) => prev.map((d) => (d.key === key ? { ...d, closed } : d)));
  };

  const setTime = (key: string, field: "open" | "close", value: string) => {
    setDays((prev) => prev.map((d) => (d.key === key ? { ...d, [field]: value } : d)));
  };

  const saveWorkingHours = async () => {
    if (provider) {
      await supabase.from("providers").update({ working_hours: days }).eq("id", provider.id);
    }
    setSavedHours(true);
    setTimeout(() => setSavedHours(false), 3000);
  };

  const saveDurations = async () => {
    if (provider) {
      await supabase.from("providers").update({ service_durations: durations }).eq("id", provider.id);
    }
    setSavedDurations(true);
    setTimeout(() => setSavedDurations(false), 3000);
  };

  const updateMaxOrders = async (v: number) => {
    setMaxOrders(v);
    if (provider) {
      await supabase.from("providers").update({ max_concurrent_orders: v }).eq("id", provider.id);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* أيام وساعات العمل */}
      <section className="rounded-[20px] bg-white p-6 shadow-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-ghasla-text-heading">أيام وساعات العمل</h2>
            <p className="mt-1 text-sm text-ghasla-text-secondary">
              حدد الأيام اللي تشتغل فيها ومواعيد الفتح والإغلاق لكل يوم
            </p>
          </div>
          <button
            type="button"
            onClick={saveWorkingHours}
            className="cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            حفظ
          </button>
        </div>

        <div className="mt-5 overflow-hidden rounded-[16px] border border-[#E4F5F5]">
          <div className="hidden grid-cols-[1fr_auto_1fr_1fr] items-center gap-4 border-b border-[#E4F5F5] bg-[#F0FDFD] px-5 py-3 sm:grid">
            <span className="text-xs font-semibold text-ghasla-text-secondary">اليوم</span>
            <span className="text-xs font-semibold text-ghasla-text-secondary">مغلق</span>
            <span className="text-xs font-semibold text-ghasla-text-secondary">الفتح</span>
            <span className="text-xs font-semibold text-ghasla-text-secondary">الإغلاق</span>
          </div>

          {days.map((d) => (
            <div
              key={d.key}
              className="grid grid-cols-1 items-center gap-4 border-b border-[#F4F8F8] px-5 py-3 last:border-b-0 sm:grid-cols-[1fr_auto_1fr_1fr]"
            >
              <div className="flex items-center justify-between sm:justify-start">
                <span className="text-sm font-medium text-ghasla-text-heading">{d.label}</span>
                <span className="flex items-center gap-2 sm:hidden">
                  <span className="text-xs text-ghasla-text-secondary">مغلق</span>
                  <Toggle size="sm" checked={d.closed} onChange={(v) => toggleClosed(d.key, v)} />
                </span>
              </div>
              <div className="hidden sm:block">
                <Toggle size="sm" checked={d.closed} onChange={(v) => toggleClosed(d.key, v)} />
              </div>
              <input
                type="time"
                className={`${inputClass} ${d.closed ? "pointer-events-none opacity-40" : ""}`}
                value={d.open}
                disabled={d.closed}
                onChange={(e) => setTime(d.key, "open", e.target.value)}
              />
              <input
                type="time"
                className={`${inputClass} ${d.closed ? "pointer-events-none opacity-40" : ""}`}
                value={d.close}
                disabled={d.closed}
                onChange={(e) => setTime(d.key, "close", e.target.value)}
              />
            </div>
          ))}
        </div>

        {savedHours && (
          <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#27AE60]">
            <i className="ri-checkbox-circle-fill" /> تم حفظ أوقات العمل
          </p>
        )}
      </section>

      {/* مدة الموعد لكل خدمة */}
      <section className="rounded-[20px] bg-white p-6 shadow-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-ghasla-text-heading">مدة الموعد لكل خدمة</h2>
            <p className="mt-1 text-sm text-ghasla-text-secondary">
              المدة الافتراضية اللي بيحجز بيها العميل موعد في كل خدمة
            </p>
          </div>
          <button
            type="button"
            onClick={saveDurations}
            className="cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            حفظ
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {serviceTypes.map((type) => {
            const meta = SERVICE_META[type] ?? { label: type, durations: [] };
            const options = meta.durations.map((d) => ({ value: d, label: d }));
            return (
              <div key={type} className="rounded-[16px] border border-[#E4F5F5] bg-[#F0FDFD] p-4">
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-ghasla-text-link">
                    <i className="ri-time-line text-base" />
                  </span>
                  <span className="text-sm font-semibold text-ghasla-text-heading">{meta.label}</span>
                </div>
                <Select
                  value={durations[type] ?? options[0]?.value ?? ""}
                  onChange={(v) => setDurations((prev) => ({ ...prev, [type]: v }))}
                  options={options}
                />
              </div>
            );
          })}
        </div>

        {savedDurations && (
          <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#27AE60]">
            <i className="ri-checkbox-circle-fill" /> تم حفظ مدد المواعيد
          </p>
        )}
      </section>

      {/* الحد الأقصى للطلبات المتزامنة */}
      <section className="rounded-[20px] bg-white p-6 shadow-card">
        <h2 className="text-xl font-semibold text-ghasla-text-heading">الحد الأقصى للطلبات المتزامنة</h2>
        <p className="mt-1 text-sm text-ghasla-text-secondary">
          عدد الطلبات اللي تقدر تشتغل عليها في نفس الوقت — بعدها مش هيوصلوك طلبات جديدة
        </p>
        <div className="mt-5 max-w-xs">
          <label className={labelClass}>عدد الطلبات</label>
          <input
            type="number"
            min={1}
            className={inputClass}
            value={maxOrders}
            onChange={(e) => updateMaxOrders(Number(e.target.value))}
          />
        </div>
      </section>
    </div>
  );
}